import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { spracheAusProfil, t } from './sprache';
import { brancheSetzen } from './branche';
import { supabase, type Mandant, type Zugang } from './supabase';
import { geraetEinloesen } from './mfaGeraet';
import { schreibschutzSetzen } from './schreibschutz';

const SPEICHER_SCHLUESSEL = 'merkan.mandant';

/** Sitzung, für die das gemerkte Gerät bereits eingelöst wurde (einmal je Sitzung) */
let eingeloest: string | null = null;

/** Mandant, an den die aufgerufene Adresse gebunden ist (z. B. lms-merkan.parlinex.io) */
export interface HostMandant {
  mandant_id: string;
  name: string;
  kurzname: string;
}

/** Subdomain aus <subdomain>-merkan.parlinex.io; andere Adressen (pages.dev, localhost) sind nicht gebunden */
function hostSubdomain(): string | null {
  if (typeof window === 'undefined') return null;
  const m = /^([a-z0-9]+(?:-[a-z0-9]+)*)-merkan\.parlinex\.io$/i.exec(window.location.hostname);
  return m ? m[1].toLowerCase() : null;
}

/** Einmal je Seitenaufruf auflösen; null = Adresse ist an keinen Mandanten gebunden */
const hostMandantLaden: Promise<HostMandant | null> = (async () => {
  const sub = hostSubdomain();
  if (!sub) return null;
  const { data } = await supabase.rpc('mandant_nach_subdomain', { p_subdomain: sub });
  const zeile = (data as HostMandant[] | null)?.[0];
  return zeile ? { mandant_id: zeile.mandant_id, name: zeile.name, kurzname: zeile.kurzname } : null;
})().catch(() => null);

export interface BenutzerEinstellungen {
  zeitraum: '7tage' | 'monat' | 'jahr';
  darstellung: 'system' | 'hell' | 'dunkel';
  praesentation: boolean;
  standard_absender: string | null;
  /** Bedienvorlieben für alle Geräte: Menü eingeklappt, Ansichten, letzter Reiter (Migration 0068) */
  oberflaeche?: Record<string, string>;
}

/** Rolle, deren Rechte die App gerade zeigt (BR-06) */
export interface AnsichtAls { rolle_id: string; name: string; rechte: string[] }

const ANSICHT_SCHLUESSEL = 'merkan.ansicht_als';

function ansichtLesen(mandantId: string | undefined): AnsichtAls | null {
  if (!mandantId) return null;
  try {
    const roh = sessionStorage.getItem(ANSICHT_SCHLUESSEL);
    const a = roh ? (JSON.parse(roh) as AnsichtAls & { mandant_id?: string }) : null;
    return a && a.mandant_id === mandantId && Array.isArray(a.rechte) ? { rolle_id: a.rolle_id, name: a.name, rechte: a.rechte } : null;
  } catch {
    return null;
  }
}

function ansichtMerken(mandantId: string | undefined, a: AnsichtAls | null) {
  try {
    if (a && mandantId) sessionStorage.setItem(ANSICHT_SCHLUESSEL, JSON.stringify({ ...a, mandant_id: mandantId }));
    else sessionStorage.removeItem(ANSICHT_SCHLUESSEL);
  } catch {
    /* ohne Speicher gilt die Ansicht nur bis zum Neuladen */
  }
}

const STANDARD_EINSTELLUNGEN: BenutzerEinstellungen = { zeitraum: 'monat', darstellung: 'system', praesentation: false, standard_absender: null, oberflaeche: {} };

interface SessionState {
  session: Session | null;
  laden: boolean;
  /** Mandantenliste des Benutzers ist geladen */
  mandantenGeladen: boolean;
  /** Mandant der aufgerufenen Adresse, falls die Adresse gebunden ist */
  hostMandant: HostMandant | null;
  /** Meldung für die Anmeldeseite, z. B. kein Zugang zum Mandanten dieser Adresse */
  hinweis: string | null;
  hinweisLoeschen: () => void;
  mandanten: Mandant[];
  mandant: Mandant | null;
  zugang: Zugang | null;
  /** Wirksame Rechte des Benutzers im gewählten Mandanten (Bereiche und Funktionen) */
  rechte: string[];
  /** Prüft ein Recht; bei mehreren genügt eines */
  hat: (...recht: string[]) => boolean;
  /** Eigene Rechte, auch während "Ansicht als" (BR-06) */
  echteRechte: string[];
  /** Ansicht als: App mit den Rechten einer Rolle ansehen, nur Anzeige */
  ansichtAls: AnsichtAls | null;
  ansichtStarten: (a: AnsichtAls) => Promise<void>;
  ansichtBeenden: () => Promise<void>;
  einstellungen: BenutzerEinstellungen;
  einstellungSetzen: (e: Partial<BenutzerEinstellungen>) => Promise<void>;
  mandantWaehlen: (id: string) => void;
  zugangPruefen: () => Promise<void>;
  abmelden: () => Promise<void>;
}

const Ctx = createContext<SessionState>({
  session: null,
  laden: true,
  mandantenGeladen: false,
  hostMandant: null,
  hinweis: null,
  hinweisLoeschen: () => {},
  mandanten: [],
  mandant: null,
  zugang: null,
  rechte: [],
  hat: () => false,
  echteRechte: [],
  ansichtAls: null,
  ansichtStarten: async () => {},
  ansichtBeenden: async () => {},
  einstellungen: STANDARD_EINSTELLUNGEN,
  einstellungSetzen: async () => {},
  mandantWaehlen: () => {},
  zugangPruefen: async () => {},
  abmelden: async () => {},
});

/** Sitzungs-ID aus dem Zugriffstoken (Claim session_id) */
function sitzungAusToken(token: string): string | null {
  try {
    const teil = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return (JSON.parse(atob(teil)) as { session_id?: string }).session_id ?? null;
  } catch {
    return null;
  }
}

function aalAusToken(token: string): string {
  try {
    const teil = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return (JSON.parse(atob(teil)) as { aal?: string }).aal ?? 'aal1';
  } catch {
    return 'aal1';
  }
}

function gespeicherterMandant(): string | null {
  try {
    return localStorage.getItem(SPEICHER_SCHLUESSEL);
  } catch {
    return null;
  }
}

function mandantMerken(id: string | null) {
  try {
    if (id) localStorage.setItem(SPEICHER_SCHLUESSEL, id);
    else localStorage.removeItem(SPEICHER_SCHLUESSEL);
  } catch {
    /* Speicher nicht verfügbar, Auswahl gilt nur für diese Sitzung */
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [mandanten, setMandanten] = useState<Mandant[]>([]);
  const [mandant, setMandant] = useState<Mandant | null>(null);
  const [zugang, setZugang] = useState<Zugang | null>(null);
  const [rechte, setRechte] = useState<string[]>([]);
  const [einstellungen, setEinstellungen] = useState<BenutzerEinstellungen>(STANDARD_EINSTELLUNGEN);
  const [laden, setLaden] = useState(true);
  const [mandantenGeladen, setMandantenGeladen] = useState(false);
  const [hostMandant, setHostMandant] = useState<HostMandant | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  useEffect(() => {
    void hostMandantLaden.then(setHostMandant);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLaden(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Nur bei Wechsel des Benutzers oder der Sitzungsstufe neu laden (nicht bei jedem Token-Refresh)
  const sitzungsSchluessel = session ? `${session.user.id}:${aalAusToken(session.access_token)}` : '';

  useEffect(() => {
    if (!session) {
      setMandanten([]);
      setMandant(null);
      setMandantenGeladen(false);
      return;
    }
    let aktiv = true;
    void (async () => {
      const [host, { data }] = await Promise.all([hostMandantLaden, supabase.rpc('meine_mandanten')]);
      if (!aktiv) return;
      let liste = (data as Mandant[] | null) ?? [];
      if (host) {
        // Gebundene Adresse: nur der Mandant dieser Adresse. Ohne Mitgliedschaft dort sofort abmelden.
        liste = liste.filter((m) => m.mandant_id === host.mandant_id);
        if (liste.length === 0) {
          setHinweis(t('Das Konto {email} hat keinen Zugang zu {mandant}. Bitte mit dem richtigen Konto anmelden.', { email: session.user.email ?? '', mandant: host.name }));
          await supabase.auth.signOut();
          return;
        }
      }
      setHinweis(null);
      setMandanten(liste);
      const gemerkt = gespeicherterMandant();
      const auswahl = liste.find((m) => m.mandant_id === gemerkt) ?? liste[0] ?? null;
      setMandant((alt) => (alt && auswahl && alt.mandant_id === auswahl.mandant_id ? alt : auswahl));
      mandantMerken(auswahl?.mandant_id ?? null);
      setMandantenGeladen(true);
    })();
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitzungsSchluessel]);

  const zugangPruefen = useCallback(async () => {
    if (!session || !mandant) {
      setZugang(null);
      setRechte([]);
      return;
    }
    const { data, error } = await supabase.rpc('mein_zugang', { p_mandant: mandant.mandant_id });
    if (error) {
      setZugang({ ok: false, grund: 'fehler', mfa_pflicht: false, aal: 'aal1', sitzung_ende: null, ip: null });
      return;
    }
    let z = data as Zugang;
    // Neue Sitzung auf einem Gerät, das die MFA-Bestätigung gemerkt hat: einlösen und neu prüfen
    const sitzung = sitzungAusToken(session.access_token);
    if (z.grund === 'mfa' && !z.mfa_erneut && z.mfa_merken_stunden && sitzung && eingeloest !== sitzung) {
      eingeloest = sitzung;
      if (await geraetEinloesen()) {
        const neu = await supabase.rpc('mein_zugang', { p_mandant: mandant.mandant_id });
        if (!neu.error) z = neu.data as Zugang;
      }
    }
    setZugang(z);
    if (z.ok) {
      const r = await supabase.rpc('meine_rechte', { p_mandant: mandant.mandant_id });
      setRechte((r.data as string[] | null) ?? []);
    } else {
      setRechte([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitzungsSchluessel, mandant]);

  useEffect(() => {
    setZugang(null);
    void zugangPruefen();
  }, [zugangPruefen]);

  // Oberflächensprache des Benutzers (Profil, gilt über alle Mandanten und Geräte)
  useEffect(() => {
    if (!session) return;
    supabase.from('profile').select('sprache').eq('id', session.user.id).maybeSingle()
      .then(({ data }) => spracheAusProfil((data as { sprache?: string } | null)?.sprache));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user.id]);

  // Benutzereinstellungen je Mandant laden
  useEffect(() => {
    if (!session || !mandant) return;
    supabase.from('benutzer_einstellungen').select('zeitraum, darstellung, praesentation, standard_absender, oberflaeche')
      .eq('mandant_id', mandant.mandant_id).eq('user_id', session.user.id).maybeSingle()
      .then(({ data }) => setEinstellungen({ ...STANDARD_EINSTELLUNGEN, ...((data as Partial<BenutzerEinstellungen>) ?? {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitzungsSchluessel, mandant?.mandant_id]);

  // Darstellung und Präsentationsmodus am Dokument setzen
  useEffect(() => {
    const el = document.documentElement;
    if (einstellungen.darstellung === 'system') el.removeAttribute('data-darstellung');
    else el.setAttribute('data-darstellung', einstellungen.darstellung);
    el.setAttribute('data-praesentation', einstellungen.praesentation ? 'an' : 'aus');
  }, [einstellungen.darstellung, einstellungen.praesentation]);

  const einstellungSetzen = useCallback(async (e: Partial<BenutzerEinstellungen>) => {
    setEinstellungen((alt) => ({ ...alt, ...e }));
    if (!session || !mandant) return;
    await supabase.from('benutzer_einstellungen').upsert({ mandant_id: mandant.mandant_id, user_id: session.user.id, ...e }, { onConflict: 'mandant_id,user_id' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitzungsSchluessel, mandant?.mandant_id]);

  // Sitzungsende und Netzwechsel erkennen: beim Zurückkehren in den Tab und alle 5 Minuten
  useEffect(() => {
    if (!session || !mandant) return;
    const pruefen = () => {
      if (document.visibilityState === 'visible') void zugangPruefen();
    };
    document.addEventListener('visibilitychange', pruefen);
    const takt = window.setInterval(pruefen, 5 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', pruefen);
      window.clearInterval(takt);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitzungsSchluessel, mandant, zugangPruefen]);

  const mandantWaehlen = (id: string) => {
    const auswahl = mandanten.find((m) => m.mandant_id === id);
    if (!auswahl) return;
    setMandant(auswahl);
    mandantMerken(id);
  };

  const abmelden = async () => {
    mandantMerken(null);
    await supabase.auth.signOut();
  };

  // Branche für Hilfsfunktionen außerhalb von React; idempotent, darum direkt beim Rendern
  brancheSetzen(mandant?.branche);

  // Ansicht als (BR-06): gilt je Tab und Mandant, endet mit Mandantenwechsel oder Abmelden
  const [ansichtAls, setAnsichtAls] = useState<AnsichtAls | null>(null);
  useEffect(() => {
    const a = session ? ansichtLesen(mandant?.mandant_id) : null;
    setAnsichtAls(a);
    schreibschutzSetzen(Boolean(a));
  }, [session, mandant?.mandant_id]);

  const ansichtStarten = useCallback(async (a: AnsichtAls) => {
    if (!mandant) return;
    await supabase.rpc('ansicht_als_protokollieren', { p_mandant: mandant.mandant_id, p_rolle: a.rolle_id, p_beginn: true });
    ansichtMerken(mandant.mandant_id, a);
    schreibschutzSetzen(true);
    setAnsichtAls(a);
  }, [mandant]);

  const ansichtBeenden = useCallback(async () => {
    const a = ansichtAls;
    ansichtMerken(undefined, null);
    schreibschutzSetzen(false);
    setAnsichtAls(null);
    if (a && mandant) await supabase.rpc('ansicht_als_protokollieren', { p_mandant: mandant.mandant_id, p_rolle: a.rolle_id, p_beginn: false });
  }, [ansichtAls, mandant]);

  // Wirksame Rechte: in der Ansicht als nur die der Rolle, und nie mehr als die eigenen Bereiche zeigen dürfen
  const wirksam = ansichtAls ? ansichtAls.rechte : rechte;
  const hat = useMemo(() => {
    const set = new Set(wirksam);
    return (...recht: string[]) => recht.some((r) => set.has(r));
  }, [wirksam]);

  const abmeldenMitAnsicht = async () => {
    if (ansichtAls) await ansichtBeenden();
    await abmelden();
  };

  return (
    <Ctx.Provider value={{ session, laden, mandantenGeladen, hostMandant, hinweis, hinweisLoeschen: () => setHinweis(null), mandanten, mandant, zugang, rechte: wirksam, hat, echteRechte: rechte, ansichtAls, ansichtStarten, ansichtBeenden, einstellungen, einstellungSetzen, mandantWaehlen, zugangPruefen, abmelden: abmeldenMitAnsicht }}>
      {children}
    </Ctx.Provider>
  );
}

export const useSession = () => useContext(Ctx);

/** Kurzform für Seiten: Mandanten-ID (immer gesetzt, sobald Seiten gerendert werden) */
export function useMandantId(): string {
  const { mandant } = useSession();
  return mandant?.mandant_id ?? '';
}
